import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Link, Outlet, createRootRoute, useRouterState } from '@tanstack/react-router';
import { Database, LayoutDashboard, Play, Users } from 'lucide-react';
import { Toaster } from 'sonner';
import { QuotaBadge } from '@/components/QuotaBadge';
import { AuthGate } from '@/features/auth/AuthGate';
import { LogoutButton } from '@/features/auth/LogoutButton';
import { cn } from '@/lib/cn';
import { EventStreamProvider, useIsLive } from '@/lib/sse/EventStreamProvider';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Une erreur réseau ponctuelle ne doit pas vider l'écran : on garde
      // la donnée affichée et on retente une fois.
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const NAV = [
  { to: '/', label: 'Tableau de bord', icon: LayoutDashboard },
  { to: '/new', label: 'Nouveau run', icon: Play },
  { to: '/runs', label: 'Runs', icon: Database },
  { to: '/leads', label: 'Leads', icon: Users },
] as const;

function LiveDot() {
  const live = useIsLive();
  return (
    <span className="flex items-center gap-2 text-xs text-secondary">
      <span
        className={cn('size-2 rounded-full', live ? 'bg-success' : 'bg-warning')}
        aria-hidden
      />
      {live ? 'Direct' : 'Rafraîchissement périodique'}
    </span>
  );
}

function Shell() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  return (
    <div className="min-h-screen bg-canvas font-sans text-primary">
      <div className="flex">
        <aside className="sticky top-0 flex h-screen w-60 shrink-0 flex-col gap-1 border-r border-hairline bg-surface p-4">
          <div className="mb-6 px-2">
            <p className="text-lg font-extrabold tracking-tight">Bakiscrap</p>
            <p className="text-xs text-secondary">Google Maps → leads qualifiés</p>
          </div>
          <nav className="flex flex-col gap-1">
            {NAV.map(({ to, label, icon: Icon }) => {
              const active = to === '/' ? pathname === '/' : pathname.startsWith(to);
              return (
                <Link
                  key={to}
                  to={to}
                  className={cn(
                    'flex items-center gap-3 rounded-control px-3 py-2 text-sm font-medium transition-colors',
                    active
                      ? 'bg-tint-blue text-primary'
                      : 'text-secondary hover:bg-sunken hover:text-primary',
                  )}
                >
                  <Icon className="size-4" aria-hidden />
                  {label}
                </Link>
              );
            })}
          </nav>
          <div className="mt-auto px-2">
            <LiveDot />
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex items-center justify-between border-b border-hairline bg-surface px-6 py-3">
            <span className="text-sm text-secondary">Portail de prospection</span>
            <div className="flex items-center gap-4">
              <QuotaBadge />
              <LogoutButton />
            </div>
          </header>
          <main className="min-w-0 flex-1 p-6">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  component: () => (
    <QueryClientProvider client={queryClient}>
      <AuthGate>
        {/* EventStreamProvider est DANS la barrière : son flux SSE tape
            /api/events, qui répond 401 sans session et ferait boucler la
            reconnexion sur l'écran de connexion. */}
        <EventStreamProvider>
          <Shell />
        </EventStreamProvider>
      </AuthGate>
      <Toaster richColors position="top-right" />
    </QueryClientProvider>
  ),
});
